import { IActionDefinitionDto, IActionFolder, IDatabaseTableModel, IDatasourceDto } from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import {
  canBrowseTableRows,
  canCreateActionFolder,
  canCreateActionIn,
  canCreateActionInFolder,
  canCreateRestEndpoint,
  canCreateSelectAction,
  canEditDatasource,
  canEditTable,
  canManageActions,
  canManageDatasources,
  canManageTables,
  canRefreshDatasource,
  hasActionRowMenu,
  hasActionsGroupMenu,
  isManagedDbType
} from "./datasource-row-actions";
import { DatasourceListRow, isManagedDatasource } from "./datasources-landing-model";

/**
 * What each row's menu offers, per kind of row, as lists of item keys — the one answer the
 * Sources tree (`data-diagram-scheme-window.tsx`) and the Datasources landing both draw from, so
 * the two surfaces cannot drift into offering different things for the same entity again.
 *
 * Only the items live here. Each surface keeps its own handlers, labels and test ids, and adds
 * what is its alone: the tree its scripting "Add … Query" entries and inline rename, the landing
 * the ER diagram and "Move out of folder" (`getDatasourceListRowMenu`).
 *
 * An empty list means the row has no menu: a surface draws no trigger for it.
 */

/** What the project role lets this user change. Read once per render: each check reads storage. */
export interface IDatasourceRowAccess {
  datasources: boolean;
  tables: boolean;
  actions: boolean;
}

export const getDatasourceRowAccess = (sd: IScreenDesignContextState): IDatasourceRowAccess => ({
  datasources: canManageDatasources(sd),
  tables: canManageTables(sd),
  actions: canManageActions(sd)
});

//  ────────────────────────────────────────────────────────────────────────────
//  Per kind of row — shared by both surfaces
//  ────────────────────────────────────────────────────────────────────────────

export type DatasourceMenuItem = "edit" | "refresh" | "rename" | "delete";

/**
 * A datasource's own row. The managed database has none: it is the project's, so it is never
 * edited, renamed or deleted from a row. While the datasource is open in its editor only Delete
 * stays — the rest would act on what the editor is holding.
 */
export const getDatasourceMenuItems = (
  datasource: IDatasourceDto,
  access: IDatasourceRowAccess,
  isOpenInEditor: boolean
): DatasourceMenuItem[] => {
  if (!access.datasources || isManagedDbType(datasource)) return [];
  if (isOpenInEditor) return ["delete"];
  const items: DatasourceMenuItem[] = [];
  if (canEditDatasource(datasource)) items.push("edit");
  if (canRefreshDatasource(datasource)) items.push("refresh");
  items.push("rename", "delete");
  return items;
};

export type ActionsGroupMenuItem = "new-action" | "new-select-action" | "new-rest-endpoint" | "new-folder";

/** The Actions group's "+". "new-action" is labelled by `getNewActionKindLabel`. */
export const getActionsGroupMenuItems = (
  datasource: IDatasourceDto,
  access: IDatasourceRowAccess
): ActionsGroupMenuItem[] => {
  if (!access.actions || !hasActionsGroupMenu(datasource)) return [];
  const items: ActionsGroupMenuItem[] = [];
  if (canCreateActionIn(datasource)) items.push("new-action");
  if (canCreateSelectAction(datasource)) items.push("new-select-action");
  if (canCreateRestEndpoint(datasource)) items.push("new-rest-endpoint");
  if (canCreateActionFolder(datasource)) items.push("new-folder");
  return items;
};

export type TablesGroupMenuItem = "new-table";

/**
 * The Tables group's "+": a new table, on the managed database only. No "New Folder": the project
 * does not save table folders (`databaseTablesFolders` is in neither the save diff nor the backend
 * model), so a folder made here would vanish on the next reload together with what it held.
 */
export const getTablesGroupMenuItems = (
  datasource: IDatasourceDto,
  access: IDatasourceRowAccess
): TablesGroupMenuItem[] => (access.tables && canEditTable(datasource) ? ["new-table"] : []);

export type ActionFolderMenuItem = "new-folder" | "rename" | "new-action" | "delete";

/**
 * An action folder, under the role that manages what it holds. Folders nest one level deep, so
 * only a top-level folder takes a sub-folder.
 */
export const getActionFolderMenuItems = (
  folder: IActionFolder,
  datasource: IDatasourceDto,
  access: IDatasourceRowAccess
): ActionFolderMenuItem[] => {
  if (!access.actions) return [];
  const items: ActionFolderMenuItem[] = [];
  if (!folder.parentFolderId) items.push("new-folder");
  items.push("rename");
  if (canCreateActionInFolder(datasource)) items.push("new-action");
  items.push("delete");
  return items;
};

export type TableFolderMenuItem = "rename" | "delete";

/** A table folder: renamed or deleted through the table-folder list. Table folders do not nest. */
export const getTableFolderMenuItems = (access: IDatasourceRowAccess): TableFolderMenuItem[] =>
  access.tables ? ["rename", "delete"] : [];

export type TableMenuItem = "browse" | "edit" | "rename" | "delete";

/**
 * A table row. Only the managed database's tables are browsed and edited here — an external
 * server's schema is read from that server — and only one in the project's own schema
 * (`isEditable`, see `isTableEditable`) is renamed or dropped; another schema's opens read-only.
 */
export const getTableMenuItems = (
  datasource: IDatasourceDto,
  access: IDatasourceRowAccess,
  isEditable: boolean
): TableMenuItem[] => {
  if (!access.tables) return [];
  const items: TableMenuItem[] = [];
  if (canBrowseTableRows(datasource)) items.push("browse");
  if (canEditTable(datasource)) {
    items.push("edit");
    if (isEditable) items.push("rename", "delete");
  }
  return items;
};

export type ActionMenuItem = "edit" | "where-used" | "delete";

export interface IActionMenuInput {
  datasource: IDatasourceDto;
  access: IDatasourceRowAccess;
  /** A template-based AI action takes its behaviour from the template and has no editor. */
  isTemplateBased: boolean;
  /** The action — or, for a REST endpoint, the endpoint — is the one its editor is showing. */
  isOpenInEditor: boolean;
}

/** An action row. Types that edit their actions elsewhere (`hasActionRowMenu`) have no menu. */
export const getActionMenuItems = (input: IActionMenuInput): ActionMenuItem[] => {
  if (!input.access.actions || !hasActionRowMenu(input.datasource)) return [];
  // "Where Used" is a READ of the action, so it survives every case that hides Edit:
  // a template-based action and one already open in its editor are exactly the two
  // moments someone most wants to know what a change would break (KOA-17818).
  if (input.isTemplateBased || input.isOpenInEditor) return ["where-used", "delete"];
  return ["edit", "where-used", "delete"];
};

//  ────────────────────────────────────────────────────────────────────────────
//  The Datasources landing's rows
//  ────────────────────────────────────────────────────────────────────────────

/** Every entry the landing's row menu can hold; its row actions are keyed by these. */
export type DatasourceListRowMenuKey =
  | "edit"
  | "where-used"
  | "browse"
  | "diagram"
  | "create-table"
  | "refresh"
  | "new-action"
  | "new-select-action"
  | "new-rest-endpoint"
  | "new-table"
  | "new-folder"
  | "rename"
  | "unfile"
  | "delete";

/** What the landing's rows need from the project, resolved once per render. */
export interface IDatasourceListRowMenuContext {
  access: IDatasourceRowAccess;
  /** `isTableEditable` for the open project. */
  isTableEditable: (table: IDatabaseTableModel) => boolean;
  /** Whether the action is a template-based AI action (`canEditAction`'s negation). */
  isTemplateAction: (action: IActionDefinitionDto) => boolean;
}

/**
 * The landing's menu for a row: the shared items above, plus what only the landing has.
 *
 * - The managed database's row opens the ER diagram, which is drawn in the landing's centre, and
 *   carries "Create table" beside its Tables group's "New Table".
 * - A filed table or action can be moved out of its folder; on the tree that is a drag.
 *
 * A row's click opens its editor exactly when its menu holds "edit", so the menu and the pointer
 * cannot disagree about whether a row opens.
 */
export const getDatasourceListRowMenu = (
  row: DatasourceListRow,
  context: IDatasourceListRowMenuContext
): DatasourceListRowMenuKey[] => {
  const access: IDatasourceRowAccess = context.access;

  if (row.kind === "datasource") {
    const items: DatasourceListRowMenuKey[] = [...getDatasourceMenuItems(row.datasource, access, false)];
    if (isManagedDatasource(row.datasource)) {
      items.push("diagram");
      if (access.tables && canEditTable(row.datasource)) items.push("create-table");
    }
    return items;
  }
  if (row.kind === "group") {
    if (row.groupKind === "actions") return getActionsGroupMenuItems(row.datasource, access);
    return getTablesGroupMenuItems(row.datasource, access);
  }
  if (row.kind === "folder") {
    if (row.folderKind === "action") return getActionFolderMenuItems(row.folder, row.datasource, access);
    return getTableFolderMenuItems(access);
  }
  // The Sources tree offers Session Variables' Edit with no role check of its own.
  if (row.kind === "sessionVariables") return ["edit"];
  if (row.kind === "table") {
    const items: DatasourceListRowMenuKey[] = [
      ...getTableMenuItems(row.datasource, access, context.isTableEditable(row.table))
    ];
    if (row.folderId && access.tables) items.push("unfile");
    return items;
  }
  if (row.kind === "action") {
    const items: DatasourceListRowMenuKey[] = [
      ...getActionMenuItems({
        datasource: row.datasource,
        access,
        isTemplateBased: context.isTemplateAction(row.action),
        // The landing is only on screen while no editor is open.
        isOpenInEditor: false
      })
    ];
    if (row.folderId && access.actions) items.push("unfile");
    return items;
  }
  // A schema header only arranges the tables below it.
  return [];
};
